import { useState, useEffect } from "react";

import {
  useSearchParams,
  useParams,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { Row, Col, Card, Layout, message, Modal } from "antd";

import QuoteHeader from "../components/QuoteHeader";
import QuoteTimeline from "../components/QuoteTimeline";
import PricingSidebar from "../components/PricingSidebar";
import EmailQuoteModal from "../components/EmailQuoteModal";
import ActivitySearchTab from "../components/ActivitySearchTab";

import {
  createQuotation,
  updateQuotation,
  getQuotation,
  downloadQuotationPdf,
  searchHotels,
 previewQuotation,
} from "@/services/quotationService";

import apiClient from "@/utils/apiClient";
import companyService from "@/services/companyService";


const { Content } = Layout;

const QuoteWorkspace = () => {
const {
  quotationId
} = useParams();

const location =
   useLocation();

const navigate =
   useNavigate();

const isEditMode =
  location.pathname.includes(
    "/edit/"
  );

const isViewMode =
  location.pathname.includes(
    "/view/"
  );

const isDuplicateMode =
  location.pathname.includes(
    "/duplicate/"
  );

const isUseMode =
  location.pathname.includes(
    "/use/"
  );

  const [searchParams] = useSearchParams();

  const leadId = searchParams.get("leadId");

  const [lead, setLead] = useState(null);

  const [companySettings, setCompanySettings] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailPayload, setEmailPayload] = useState(null);
  const [activitySearchOpen, setActivitySearchOpen] = useState(false); 


  const [transportInventory, setTransportInventory] =
    useState([]);

useEffect(() => {

  if (!quotationId) return;

  fetchQuotation();
  loadVehicleOptions();
}, [quotationId]);

const loadVehicleOptions = async () => {

  try {

    const res = await apiClient.get(
      "/transport-plan/inventory?limit=500"
    );

    const routes =
      res?.data?.data || [];

    setTransportInventory(routes);

    const vehicles = [
      ...new Set(
        routes
          .flatMap(
            (r) => r.vehiclePricing || []
          )
          .map(
            (v) => v.vehicleType
          )
          .filter(Boolean)
      )
    ];

    setVehicleOptions(
      vehicles.sort()
    );

  } catch (err) {

    console.error(
      "Vehicle list load failed",
      err
    );

  }

};

const fetchQuotation = async () => {

  try {

    const response =
      await getQuotation(
      quotationId
);

    const quotation =
      response.data?.data;


    if (!quotation) {

      message.error(
        "Quotation not found"
      );

      return;
    }

console.log(
  "Loaded quotation",
  quotation
);


/* =====================================
   HYDRATE LEAD
===================================== */

setLead({

  _id:
    quotation.leadId || null,

  assignedTo:
    quotation.assignedTo || null,

  createdBy:
    quotation.createdBy || null,

  customerName:
    quotation.customerName || "",

  email:
    quotation.customerEmail || "",

  phone:
    quotation.customerPhone || "",

  destination:
    quotation.destination || {},

  duration:
    quotation.duration ||
    quotation.durationDays ||
    0,

  pax:
    quotation.travelers || {
      adults: 1,
      children: 0,
      infants: 0,
    },
});

/* =====================================
   HYDRATE DAYS
===================================== */

if (
  quotation.itinerary?.length
) {

  const mappedDays =
    quotation.itinerary.map(
      (day, index) => ({

        id:
          day._id ||
          index + 1,

        title:
          day.title || "",

        date:
          day.date || "",

        city:
          day.city || "",

        hotels:
          day.hotels || [],

        transport:
          day.transport || [],

        activities:
          day.activities || [],

        specialInclusions:
          day.specialInclusions || [],

        notes:
          day.notes || "",
      })
    );

  setDays(mappedDays);
}

if (
  quotation.pricing ||
  quotation.pricingSnapshot
) {
  const backendPricing =
    quotation.pricing || {};

  const pricingSnapshot =
    quotation.pricingSnapshot || {};

  const subtotal = Number(
    backendPricing.subtotal ??
    pricingSnapshot.basePrice ??
    0
  );

  const gstAmount = Number(
    backendPricing.gstAmount ??
    backendPricing.taxes ??
    pricingSnapshot.gstAmount ??
    pricingSnapshot.taxes ??
    0
  );

  const markupAmount = Number(
    backendPricing.markupAmount ??
    pricingSnapshot.markupAmount ??
    0
  );

  const discount = Number(
    backendPricing.discount ??
    pricingSnapshot.discount ??
    quotation.manualDiscount ??
    0
  );

  const grandTotal = Number(
    backendPricing.grandTotal ??
    backendPricing.total ??
    pricingSnapshot.grandTotal ??
    pricingSnapshot.totalPrice ??
    Math.max(
      subtotal +
        markupAmount +
        gstAmount -
        discount,
      0
    )
  );

  setPricing({

    hotelTotal: Number(
      backendPricing.hotelTotal ??
      pricingSnapshot.hotelTotal ??
      0
    ),

    transportTotal: Number(
      backendPricing.transportTotal ??
      pricingSnapshot.transportTotal ??
      0
    ),

    activitiesTotal: Number(
      backendPricing.activitiesTotal ??
      pricingSnapshot.activitiesTotal ??
      0
    ),

    inclusionsTotal: Number(
      backendPricing.inclusionsTotal ??
      pricingSnapshot.inclusionsTotal ??
      0
    ),

    subtotal,

    subtotalWithTax: Number(
      backendPricing.subtotalWithTax ??
      subtotal +
        markupAmount +
        gstAmount
    ),

    markupAmount,

    discount,

    gstAmount,

    taxes: gstAmount,

    gstPercent: Number(
      backendPricing.gstPercent ??
      pricingSnapshot.gstPercent ??
      quotation.gstPercentage ??
      0
    ),

    total: grandTotal,

    grandTotal,

    currency:
      backendPricing.currency ||
      pricingSnapshot.currency ||
      "INR",
  });
}

/* =====================================
   HYDRATE PRICING SETTINGS
===================================== */

setQuotationSettings(
  (prev) => ({

    ...prev,

markup:
  quotation.markup?.percentage ??
  prev.markup ??
  0,

gst:
  quotation.gstPercentage ??
  quotation.pricing?.gstPercent ??
  quotation.pricingSnapshot?.gstPercent ??
  prev.gst ??
  0,

manualDiscount:
  quotation.manualDiscount ??
  quotation.pricing?.discount ??
  quotation.pricingSnapshot?.discount ??
  0,

    proposalTitle:
      quotation.meta?.proposalTitle || "",

    proposalSubtitle:
      quotation.meta?.proposalSubtitle || "",

    inclusions:
      quotation.meta?.inclusions || [],

    exclusions:
      quotation.meta?.exclusions || [],
  })
);

/* =====================================
   HYDRATE QUOTATION EXPIRY
===================================== */

if (
  !isDuplicateMode &&
  !isUseMode
) {
  if (quotation?.expiresAt) {
    const expiryDate =
      new Date(
        quotation.expiresAt
      );

    if (
      Number.isFinite(
        expiryDate.getTime()
      )
    ) {
      setQuotationExpiry(
        expiryDate.toISOString()
      );

      setIsExpired(
        expiryDate.getTime() <=
          Date.now()
      );
    } else {
      console.error(
        "Invalid quotation expiresAt:",
        quotation.expiresAt
      );

      setQuotationExpiry(null);
      setRemainingTime(0);
      setIsExpired(false);
    }
  } else {
    setQuotationExpiry(null);
    setRemainingTime(0);
    setIsExpired(false);
  }
}

/* =====================================
   HYDRATE OFFER
===================================== */
if (
  quotation.offerId &&

  !isDuplicateMode &&

  !isUseMode
) {

  setSelectedOffer(
    quotation.offerId
  );
}

/* =====================================
   DUPLICATE MODE
===================================== */
if (isDuplicateMode) {

  setSelectedOffer(null);

  setQuotationExpiry(null);

  setRemainingTime(0);

  setIsExpired(false);
}

/* =====================================
   USE QUOTE MODE
===================================== */

if (isUseMode) {

  setSelectedOffer(null);

  setQuotationExpiry(null);

  setRemainingTime(0);

  setIsExpired(false);
}


  } catch (err) {

    console.error(
      "Quotation fetch failed",
      err
    );

    message.error(
      "Failed to load quotation"
    );
  }
};

  useEffect(() => {
    const fetchLead = async () => {
      try {
        if (!leadId) return;

        const response = await apiClient.get(`/leads/${leadId}`);

        const leadData = response.data?.data || response.data;

        setLead({
          ...leadData,

          destinationSnapshot:
            leadData?.destinationSnapshot || leadData?.destination?.name || "",

          pax: {
            adults: leadData?.pax?.adults || 1,

            children: leadData?.pax?.children || 0,

            infants: leadData?.pax?.infants || 0,
          },
        });

try {

  const settingsResponse = await apiClient.get(
    "/settings"
  );

  const settingsData =
    settingsResponse.data?.data ||
    settingsResponse.data;

  setCompanySettings(settingsData);

  try {
    const profileRes = await companyService.getProfile();
    if (profileRes?.data?.data) {
      setCompanyProfile(profileRes.data.data);
    }
  } catch (pErr) {
    console.error("Company profile fetch error", pErr);
  }

  setQuotationSettings((prev) => ({
    ...prev,

    inclusions:
      settingsData?.defaultInclusions ||

      settingsData?.settings
        ?.quotationDefaults
        ?.inclusions ||

      [],

    exclusions:
      settingsData?.defaultExclusions ||

      settingsData?.settings
        ?.quotationDefaults
        ?.exclusions ||

      [],
  }));

} catch (settingsError) {

  console.error(
    "Company settings fetch failed",
    settingsError
  );
}

/* =========================================
   FETCH ACTIVE OFFERS
========================================= */

try {

const offersResponse =
  await apiClient.get(
    "/offers/active"
  );

  const offersData =
    offersResponse.data?.data ||
    offersResponse.data ||
    [];


const activeOffers =
  offersData.filter((offer) => {

    if (
      offer?.status !== "active"
    ) {
      return false;
    }

    if (
      offer?.validUntil &&
      new Date(
        offer.validUntil
      ) < new Date()
    ) {
      return false;
    }

    return true;
  });

  setOffers(activeOffers);

} catch (offerError) {

  console.error(
    "Offers fetch failed",
    offerError
  );
}

      } catch (err) {
        console.error("Lead fetch failed", err);
      }
    };

    if (!quotationId) {
  fetchLead();
}
  }, [leadId]);

  /* =========================================
     DAY-WISE ITINERARY
  ========================================= */

  const [days, setDays] = useState([
    {
      id: 1,

      title: "",

      date: "",

      city: "",

      hotels: [],

      transport: [],

      activities: [],

      specialInclusions: [],

      notes: "",
    },
  ]);

  /* =========================================
     OFFER STATE
  ========================================= */

  const [selectedOffer, setSelectedOffer] = useState(null);

  const [offers, setOffers] = useState([]);

const [quotationExpiry, setQuotationExpiry] =
  useState(null);

const [remainingTime, setRemainingTime] =
  useState(0);

const [isExpired, setIsExpired] =
  useState(false);

  /* =========================================
     GLOBAL TRANSPORT
  ========================================= */

  const [globalTransportConfig, setGlobalTransportConfig] = useState({
    vehicleType: "",

    applySameCab: true,
  });

const [vehicleOptions, setVehicleOptions] = useState([]);

  /* =========================================
     SETTINGS
  ========================================= */

  const [quotationSettings, setQuotationSettings] = useState({
    currency: "INR",

    includePolicies: true,

    includeHotels: true,

    includeTransport: true,

    includeSightseeing: true,

    includeInclusions: true,

    includeExclusions: true,

    includePaymentPolicy: true,

    includeCancellationPolicy: true,

    includeTerms: true,

    proposalTitle: "",

    proposalSubtitle: "",

    markup: 0,

    gst: 0,

    manualDiscount: 0,

    inclusions: [],

    exclusions: [],
  });

  /* =========================================
     ADD DAY
  ========================================= */

  const addDay = () => {
    setDays((prev) => [
      ...prev,

      {
        id: Date.now(),

        title: "",

        date: "",

        city: "",

        hotels: [],

        transport: [],

        activities: [],

        specialInclusions: [],

        notes: "",
      },
    ]);
  };

  /* =========================================
     REMOVE DAY
  ========================================= */

  const removeDay = (id) => {
    setDays((prev) => prev.filter((day) => day.id !== id));
  };

  /* =========================================
     UPDATE DAY
  ========================================= */

  const updateDay = (id, updatedData) => {
    setDays((prev) =>
      prev.map((day) =>
        day.id === id
          ? {
              ...day,
              ...updatedData,
            }
          : day,
      ),
    );
  };


  /* =========================================
   PRICING STATE
========================================= */

const [pricing, setPricing] = useState({
  hotelTotal: 0,
  transportTotal: 0,
  activitiesTotal: 0,
  inclusionsTotal: 0,

  subtotal: 0,
  subtotalWithTax: 0,

  markupAmount: 0,

  discount: 0,

  gstAmount: 0,
  taxes: 0,
  gstPercent: 0,

  total: 0,
  grandTotal: 0,

  currency: "INR",
});

  const [costing, setCosting] = useState({});

  const [breakdown, setBreakdown] = useState({});

/* =========================================
   LIVE BACKEND PRICING PREVIEW
========================================= */

useEffect(() => {
  if (!Array.isArray(days)) {
    return;
  }

  if (!days.length) {
    return;
  }

  let cancelled = false;

  const timer = setTimeout(
    async () => {
      try {
        const hotels =
          days.flatMap(
            (day) =>
              Array.isArray(day.hotels)
                ? day.hotels
                : []
          );

        const transport =
          days.flatMap(
            (day) =>
              Array.isArray(day.transport)
                ? day.transport
                : []
          );

        const activities =
          days.flatMap(
            (day) =>
              Array.isArray(day.activities)
                ? day.activities
                : []
          );

        const specialInclusions =
          days.flatMap(
            (day) =>
              Array.isArray(
                day.specialInclusions
              )
                ? day.specialInclusions
                : []
          );

        /*
         * Send the same itinerary structure
         * that the backend preview controller
         * now understands.
         */
        const response =
          await previewQuotation({
            itinerary: days,

            hotels,

            transport,

            activities,

            specialInclusions,

            additionalServices: [],

            customServices: [],

            markup: {
              percentage:
                Number(
                  quotationSettings?.markup ||
                    0
                ),

              fixedAmount: 0,
            },

            gstPercentage:
              Number(
                quotationSettings?.gst ||
                  0
              ),

            manualDiscount:
              Number(
                quotationSettings?.manualDiscount ||
                  0
              ),

            offerId:
              selectedOffer?._id ||
              selectedOffer?.id ||
              null,

            travelers: {
              adults:
                Number(
                  lead?.pax?.adults ||
                    lead?.travelers?.adults ||
                    1
                ),

              children:
                Number(
                  lead?.pax?.children ||
                    lead?.travelers?.children ||
                    0
                ),

              infants:
                Number(
                  lead?.pax?.infants ||
                    lead?.travelers?.infants ||
                    0
                ),
            },
          });

        if (cancelled) {
          return;
        }

        /*
         * Backend returns:
         *
         * {
         *   success: true,
         *   data: calculated.pricing
         * }
         */
        const calculated =
          response?.data?.data || {};

        const hotelTotal =
          Number(
            calculated.hotelTotal || 0
          );

        const transportTotal =
          Number(
            calculated.transportTotal || 0
          );

        const activitiesTotal =
          Number(
            calculated.activitiesTotal || 0
          );

        const inclusionsTotal =
          Number(
            calculated.inclusionsTotal || 0
          );

        const subtotal =
          Number(
            calculated.subtotal || 0
          );

        const subtotalWithTax =
          Number(
            calculated.subtotalWithTax ||
              0
          );

        const markupAmount =
          Number(
            calculated.markupAmount || 0
          );

        const gstAmount =
          Number(
            calculated.gstAmount ??
              calculated.taxes ??
              0
          );

        const discount =
          Number(
            calculated.discount || 0
          );

        const grandTotal =
          Number(
            calculated.grandTotal ??
              calculated.total ??
              0
          );

        const gstPercent =
          Number(
            calculated.gstPercent ??
              quotationSettings?.gst ??
              0
          );

        setPricing({
          hotelTotal,

          transportTotal,

          activitiesTotal,

          inclusionsTotal,

          subtotal,

          subtotalWithTax,

          markupAmount,

          discount,

          gstAmount,

          taxes:
            gstAmount,

          gstPercent,

          total:
            grandTotal,

          grandTotal,

          currency:
            calculated.currency ||
            quotationSettings?.currency ||
            "INR",
        });

      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error(
          "LIVE PRICING PREVIEW FAILED:",
          error?.response?.data ||
            error
        );
      }
    },
    350
  );

  return () => {
    cancelled = true;

    clearTimeout(timer);
  };
}, [
  days,

  quotationSettings?.markup,

  quotationSettings?.gst,

  quotationSettings?.manualDiscount,

  quotationSettings?.currency,

  selectedOffer,

  lead?.pax?.adults,

  lead?.pax?.children,

  lead?.pax?.infants,
]);

useEffect(() => {
  if (!quotationExpiry) {
    setRemainingTime(0);
    setIsExpired(false);
    return;
  }

  const expiryMs =
    new Date(quotationExpiry).getTime();

  if (!Number.isFinite(expiryMs)) {
    console.error(
      "Invalid quotation expiry:",
      quotationExpiry
    );

    setRemainingTime(0);
    setIsExpired(false);
    return;
  }

  const updateTimer = () => {
    const remainingMs =
      expiryMs - Date.now();

    const totalSeconds = Math.max(
      0,
      Math.floor(remainingMs / 1000)
    );

    setRemainingTime(totalSeconds);

    if (totalSeconds <= 0) {
      setIsExpired(true);
      setSelectedOffer(null);
      return false;
    }

    setIsExpired(false);
    return true;
  };

  // Run immediately instead of waiting 1 second
  updateTimer();

  const timer = setInterval(() => {
    const active = updateTimer();

    if (!active) {
      clearInterval(timer);
    }
  }, 1000);

  return () => {
    clearInterval(timer);
  };
}, [quotationExpiry]);


  /* =========================================
   IMAGE HELPERS
========================================= */

  const extractImageUrl = (source) => {
    if (!source) {
      return "";
    }

    /* =========================
     STRING URL
  ========================= */

    if (typeof source === "string") {
      // Full external URL
      if (source.startsWith("http://") || source.startsWith("https://")) {
        return source;
      }

      // Local uploads path
      if (source.startsWith("/uploads")) {
        return `${window.location.origin}${source}`;
      }

      return source;
    }

    /* =========================
     OBJECT URL
  ========================= */

if (typeof source === "object") {
const image =
  source.url ||
  source.secure_url ||
  source.image ||
  source.heroImage ||
  source.coverImage ||
  source.bannerImage ||
  source.thumbnail ||
  source.path ||
  source.images?.find((img) => img?.isPrimary)?.url ||
  source.images?.[0]?.url ||
  source.images?.[0] ||
  "";

  // External URL
  if (
    typeof image === "string" &&
    (image.startsWith("http://") || image.startsWith("https://"))
  ) {
    return image;
  }

  // Uploaded local image
  if (
    typeof image === "string" &&
    image.startsWith("/uploads")
  ) {
    return `${window.location.origin}${image}`;
  }

  return image;
}

return "";
};

const resolveHotelImage = (hotel = {}) => {

  if (!hotel) {
    return "";
  }

  // direct quotation image
  const direct =
    extractImageUrl(hotel.image);

  if (direct) {
    return direct;
  }

  // inventory hotel image
const inventoryImage =
  extractImageUrl({
    image: hotel?.hotelId?.image,
    heroImage: hotel?.hotelId?.heroImage,
    coverImage: hotel?.hotelId?.coverImage,
    bannerImage: hotel?.hotelId?.bannerImage,
    images: hotel?.hotelId?.images,
  });

if (inventoryImage) {
  return inventoryImage;
}


  // inventory primary image
  const inventoryPrimary =
    hotel?.hotelId?.images?.find(
      (img) => img?.isPrimary
    );

  if (inventoryPrimary) {
    return extractImageUrl(
      inventoryPrimary
    );
  }

  // inventory first image
  if (
    hotel?.hotelId?.images?.length
  ) {
    return extractImageUrl(
      hotel.hotelId.images[0]
    );
  }

  // local primary image
  const primary =
    hotel?.images?.find(
      (img) => img?.isPrimary
    );

  if (primary) {
    return extractImageUrl(primary);
  }

  // local first image
  if (hotel?.images?.length) {
    return extractImageUrl(
      hotel.images[0]
    );
  }

  return "";
};

const resolveTransportImage = (transport) => {

  if (!transport) return "";

  // Image already present on quotation
  const direct =
    extractImageUrl(transport.image);

  if (direct) return direct;

  // Find matching transport from inventory
  const matchedRoute =
    transportInventory.find((route) => {

      return (

        (route.source || "")
          .trim()
          .toLowerCase() ===
        (transport.source || "")
          .trim()
          .toLowerCase()

        &&

        (route.destination || "")
          .trim()
          .toLowerCase() ===
        (transport.destination || "")
          .trim()
          .toLowerCase()

        &&

        (route.serviceType || "")
          .trim()
          .toLowerCase() ===
        (transport.serviceType || "")
          .trim()
          .toLowerCase()

      );

    });

  if (matchedRoute?.image) {

    const inventoryImage =
      extractImageUrl(
        matchedRoute.image
      );

    if (inventoryImage)
      return inventoryImage;
  }

  // Vehicle image fallback
  const vehicleImage =
    extractImageUrl(
      transport?.vehicle?.image
    );

  if (vehicleImage)
    return vehicleImage;

  if (transport?.vehicle?.images?.length) {

    return extractImageUrl(
      transport.vehicle.images[0]
    );

  }

  return "";

};

const resolveDestinationImage = () => {
  if (lead?.destination && typeof lead.destination === "object") {
    const destinationImage =
      extractImageUrl(lead.destination) ||
      extractImageUrl(lead.destination.image) ||
      extractImageUrl(lead.destination.heroImage) ||
      extractImageUrl(lead.destination.coverImage) ||
      extractImageUrl(lead.destination.bannerImage);

    if (destinationImage) {
      return destinationImage;
    }
  }

  const flatImage = extractImageUrl(lead?.destinationImage);

  if (flatImage) {
    return flatImage;
  }

  const hotelFallback = resolveHotelImage(days?.[0]?.hotels?.[0]);

  if (hotelFallback) {
    return hotelFallback;
  }

  const transportFallback = resolveTransportImage(days?.[0]?.transport?.[0]);

  if (transportFallback) {
    return transportFallback;
  }

  return "";
};

  /* =========================================
     BUILD PAYLOAD
  ========================================= */

const buildPayload = () => {
const validDates =
  days
    .map((d) => d.date)
    .filter(Boolean);

const activeDays =
  days.filter(
    (day) =>
      day.date ||
      day.city ||
      day.hotels?.length ||
      day.transport?.length ||
      day.activities?.length ||
      day.notes
  );

const durationDays =
  activeDays.length;

const durationNights =
  Math.max(
    0,
    durationDays - 1
  );

const durationText =
  durationDays > 0
    ? `${durationNights} Nights / ${durationDays} Days`
    : "";

return {
  leadId:
    lead?._id ||
    leadId,

    offerId:
      selectedOffer?._id || null,

    markup: {
      percentage: Number(
        quotationSettings?.markup || 0
      ),

      fixedAmount: 0,
    },

    gstPercentage:
      Number(
        quotationSettings?.gst || 0
      ),

    manualDiscount:
      Number(
        quotationSettings?.manualDiscount || 0
      ),

    consultant: {
      name:
        lead?.assignedTo?.name ||
        lead?.createdBy?.name ||
        "",

      email:
        lead?.assignedTo?.email ||
        lead?.createdBy?.email ||
        "",

      phone:
        lead?.assignedTo?.phone ||
        lead?.createdBy?.phone ||
        "",
    },

    customerName:
      lead?.customerName ||
      lead?.name ||
      "",

    customerEmail:
      lead?.email || "",

    customerPhone:
      lead?.phone || "",

destination:
  typeof lead?.destination === "object"
    ? {
        _id:
          lead?.destination?._id || "",

        name:
          lead?.destination?.name ||

          lead?.destination?.title ||

          lead?.destination?.city ||

          "",

        city:
          lead?.destination?.city || "",

        country:
          lead?.destination?.country || "",

        currency:
          lead?.destination?.currency ||
          "INR",

        image:
          extractImageUrl(
            lead?.destination?.image
          ) ||

          extractImageUrl(
            lead?.destination?.heroImage
          ) ||

          extractImageUrl(
            lead?.destination?.coverImage
          ) ||

          resolveDestinationImage() ||

          "",

        heroImage:
          extractImageUrl(
            lead?.destination?.heroImage
          ) ||

          extractImageUrl(
            lead?.destination?.image
          ) ||

          resolveDestinationImage() ||

          "",

        coverImage:
          extractImageUrl(
            lead?.destination?.coverImage
          ) || "",
      }
    : {
        name:
          lead?.destination ||

          lead?.destinationSnapshot ||

          days?.[0]?.city ||

          "",

        image:
          resolveDestinationImage() ||

          "",

        heroImage:
          resolveDestinationImage() ||

          "",
      },

travelers: {
  adults: lead?.pax?.adults || 1,

  children: lead?.pax?.children || 0,

  infants: lead?.pax?.infants || 0,

  childWithBed:
    days?.[0]?.hotels?.[0]
      ?.occupancy?.childWithBed || 0,

  childNoBed:
    days?.[0]?.hotels?.[0]
      ?.occupancy?.childNoBed || 0,

  extraAdults:
    days?.[0]?.hotels?.[0]
      ?.occupancy?.extraAdults || 0,
},

      dateRange: days.map((d) => d.date).filter(Boolean),

      durationText,

duration:
  durationDays,

durationDays,

durationNights,



      hotels: days.flatMap((day) =>
        (day.hotels || []).map((hotel) => ({
          ...hotel,

          image: resolveHotelImage(hotel),

          checkIn: hotel.checkIn || day.date || "",

          checkOut: hotel.checkOut || day.date || "",

          rooms: hotel.rooms || 1,

          roomCategory: hotel.roomCategory || hotel.roomType || "",

          occupancy: hotel.occupancy || {
            adults: 1,

            extraAdults: 0,

            childWithBed: 0,

            childNoBed: 0,
          },

          total: Number(hotel.total || 0),
        })),
      ),

      transport: days.flatMap((day) =>
        (day.transport || []).map((transport) => ({
          ...transport,

          image: resolveTransportImage(transport),

          vehicleType: transport.vehicle || transport.vehicleType || "",

          route: transport.routeName || "",

          fromLocation: transport.source || "",

          toLocation: transport.destination || "",

          passengers: lead?.pax?.adults || 1,
        })),
      ),

      itinerary: days
  .filter(
    (day) =>
      day.date ||
      day.city ||
      day.hotels?.length ||
      day.transport?.length ||
      day.activities?.length ||
      day.notes
  )
  .map((day, index) => ({
        dayNumber: index + 1,

        title:
          day.title ||
          day.transport?.[0]?.routeName ||
          (day.city ? `Explore ${day.city}` : "") ||
          `Day ${index + 1}`,

        description:
          day.notes ||
          day.transport?.[0]?.itinerary ||
          (day.hotels?.[0] ? `Stay at ${day.hotels[0].name}` : "") ||
          "",

        date: day.date || "",

        city: day.city || "",

heroImage:

  resolveTransportImage(
    day.transport?.[0]
  ) ||

  day.transport?.[0]?.image ||

  "",


        transport: (day.transport || []).map((transport) => ({
          ...transport,

          image: resolveTransportImage(transport),
        })),

        hotels: (day.hotels || []).map((hotel) => ({
          ...hotel,

          image: resolveHotelImage(hotel),

          total: Number(hotel.total || 0),
        })),

        activities: (day.activities || []).map((activity) => ({
          ...activity,

          quantity: Number(activity.quantity || 1),

          price: Number(activity.price || 0),

          total: Number(activity.total || 0),
        })),

        specialInclusions: (day.specialInclusions || []).map((item) => ({
          title: item.title || item.name || "",

          name: item.name || item.title || "",

          category: item.category || "",

          quantity: Number(item.quantity || 1),

          price: Number(item.price || 0),

          total: Number(
            item.total || Number(item.price || 0) * Number(item.quantity || 1),
          ),

          notes: item.notes || "",

          description: item.description || "",
        })),

        notes: day.notes || "",
      })),


      meta: {
        proposalTitle: quotationSettings?.proposalTitle || "",

        proposalSubtitle: quotationSettings?.proposalSubtitle || "",

        inclusions: quotationSettings?.includeInclusions
          ? [
              ...(quotationSettings?.inclusions || []),

              ...days.flatMap((day) =>
                (day.specialInclusions || [])

                  .map((item) => item?.title || item?.name || "")

                  .filter(Boolean),
              ),
            ]
          : [],

        exclusions: quotationSettings?.includeExclusions
          ? quotationSettings?.exclusions || []
          : [],

        notes: quotationSettings?.notes || "",

terms:
  quotationSettings?.includeTerms
    ? (
        companySettings
          ?.termsAndConditions
          ?.content || ""
      )
    : "",

cancellationPolicy:
  quotationSettings?.includeCancellationPolicy
    ? (
        companySettings?.refundPolicy || ""
      )
    : "",

paymentPolicy:
  quotationSettings?.includePaymentPolicy
    ? (
        companySettings?.paymentPolicy || ""
      )
    : "",

 },
    };
  };

  /* =========================================
     SAVE
  ========================================= */

  const handleSaveDraft = async () => {
if (isViewMode) {

  message.warning(
    "Preview mode is read only"
  );

  return;
}   

 try {

if (
  selectedOffer &&
  isExpired
) {

  message.error(
    "Offer validity expired"
  );

  return;
}  

    const payload = buildPayload();

console.log("========== HOTEL PAYLOAD ==========");

console.table(
  payload.hotels.map(h => ({
    hotel: h.name,
    rooms: h.rooms,
    checkIn: h.checkIn
  }))
);

console.log("========== ITINERARY ==========");

payload.itinerary.forEach(day => {
  console.log("DAY", day.dayNumber);

  console.table(
    (day.hotels || []).map(h => ({
      hotel: h.name,
      rooms: h.rooms
    }))
  );
});

      const shouldUpdateQuotation =

  quotationId &&

  !isDuplicateMode &&

  !isUseMode;

const response = shouldUpdateQuotation

  ? await updateQuotation(
      quotationId,
      payload
    )

  : await createQuotation(
      payload
    );

const savedQuotation =
  response?.data?.data;

message.success(
  shouldUpdateQuotation
    ? "Quotation updated"
    : "Quotation created"
);

if (
  !shouldUpdateQuotation &&
  savedQuotation?._id
) {
  navigate(
    `/quotation-builder/edit/${savedQuotation._id}`,
    {
      replace: true,
    }
  );
}

if (
  response?.data?.data
    ?.expiresAt
) {

  setQuotationExpiry(
    response.data.data
      .expiresAt
  );

  setIsExpired(false);
}

      return response?.data?.data;
    } catch (error) {
      console.error("Save quotation failed FULL:", {
        status: error?.response?.status,

        data: error?.response?.data,
      });

      message.error(
        error?.response?.data?.message || "Failed to save quotation",
      );

      throw error;
    }
  };

  /* =========================================
     PDF
  ========================================= */

const handleGeneratePdf = async () => {
  try {

    if (
      selectedOffer &&
      isExpired
    ) {

      message.error(
        "Offer validity expired"
      );

      return;
    }

    const savedQuotation =
      quotationId
        ? { _id: quotationId }
        : await handleSaveDraft();

    const finalQuotationId =
      savedQuotation?._id;

    if (!finalQuotationId) {

      message.error(
        "Quotation ID missing"
      );

      return;
    }

    const response =
      await downloadQuotationPdf(
        finalQuotationId
      );

    const blob =
      new Blob(
        [response.data],
        {
          type:
            "application/pdf",
        }
      );

    const url =
      window.URL.createObjectURL(
        blob
      );

    const link =
      document.createElement("a");

    link.href = url;

    link.download = `${
      lead?.customerName ||
      "quotation"
    }.pdf`;

    link.click();

    window.URL.revokeObjectURL(
      url
    );

  } catch (error) {

    console.error(
      "PDF generation failed:",
      error
    );

    message.error(
      "Failed to generate PDF"
    );
  }
};


  const handleCopyEmailQuote = async () => {
    if (isViewMode) {
      message.info("Preview mode active");
    }
    try {
      if (selectedOffer && isExpired) {
        message.error("Offer validity expired");
        return;
      }

      const payload = buildPayload();
      setEmailPayload(payload);
      setEmailModalOpen(true);
    } catch (error) {
      console.error(error);
      message.error("Failed to prepare email quotation");
    }
  };

  return (

    <Layout
      style={{
        minHeight: "100vh",
        background: "#f3f5f9",
      }}
    >

<Content
  style={{
    padding: "12px 10px",
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  }}
>

<QuoteHeader
  lead={lead}

  days={days}

vehicleOptions={
    vehicleOptions
  }

mode={
  isViewMode
    ? "view"

    : isDuplicateMode
    ? "duplicate"

    : isUseMode
    ? "use"

    : "edit"
}

  pricing={pricing}

  selectedOffer={selectedOffer}

remainingTime={
  remainingTime
}

isExpired={
  isExpired
}

  globalTransportConfig={
    globalTransportConfig
  }

  setGlobalTransportConfig={
    setGlobalTransportConfig
  }

setVehicleOptions={
  setVehicleOptions
}

  quotationSettings={
    quotationSettings
  }

  setQuotationSettings={
    setQuotationSettings
  }

  onSaveDraft={
    handleSaveDraft
  }

  onGeneratePdf={
    handleGeneratePdf
  }

  onCopyEmailQuote={
    handleCopyEmailQuote
  }
  onOpenActivitySearch={() => setActivitySearchOpen(true)}
/>


<div
  style={{
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  }}
>
<Row
  gutter={[16,16]}
  wrap={true}
  style={{
    width: "100%",
    alignItems: "flex-start",
  }}
>

  <Col
    span={24}
  >
    <QuoteTimeline
      lead={lead}
      days={days}
      addDay={addDay}
      removeDay={removeDay}
      updateDay={updateDay}
      globalTransportConfig={globalTransportConfig}
      vehicleOptions={vehicleOptions}
      quotationSettings={quotationSettings}
      setQuotationSettings={setQuotationSettings}
      companySettings={companySettings}
      onOpenActivitySearch={() => setActivitySearchOpen(true)}
      searchHotels={searchHotels}
    />
  </Col>

  <Col
    span={24}
  >
    <PricingSidebar
      mode={
        isViewMode
          ? "view"
          : isDuplicateMode
          ? "duplicate"
          : isUseMode
          ? "use"
          : "edit"
      }
      readOnly={isViewMode}
      pricing={pricing}
      selectedOffer={selectedOffer}
      setSelectedOffer={setSelectedOffer}
      offers={offers}
      quotationSettings={quotationSettings}
      setQuotationSettings={setQuotationSettings}
      remainingTime={remainingTime}
      isExpired={isExpired}
    />
  </Col>

  </Row>
</div>

<EmailQuoteModal
  open={emailModalOpen}
  onClose={() => setEmailModalOpen(false)}
  payload={emailPayload}
  lead={lead}
  days={days}
  pricing={pricing}
  companyProfile={companyProfile}
  companySettings={companySettings}
  quotationSettings={quotationSettings}
/>

<ActivitySearchTab
  open={activitySearchOpen}
  onClose={() => setActivitySearchOpen(false)}
  days={days}
  updateDay={updateDay}
  defaultDestination={lead?.destination?.name || ""}
/>

</Content>
    </Layout>
  );
};


export default QuoteWorkspace;
